import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, MessageSquare, Settings, Shield, Brain, Sparkles, Heart, Zap, TrendingUp, Download, BookOpen, Star, ArrowRight, Github, FileText, LayoutDashboard } from 'lucide-react';
import { Personality, Component, AvailableComponent, ToneOption } from './types';
import {
  generatePersonalityJSON,
  generatePersonalityPrompt,
  generatePythonCode,
  downloadFile,
  transformToOfficialSchema,
  generatePreviewResponse
} from './utils/generators';
import { getTemplates, applyTemplate, Template } from './utils/templates';
import { getPersonalityCompleteness } from './utils/validators';
import { testPersonality } from './services/api';

const createInitialPersonality = (): Personality => ({
  name: 'My Personality',
  version: '1.0.0',
  description: 'Custom AI personality',
  language: 'en' as const,
  advanced_parameters: {
    empathy: 0.5,
    formality: 0.5,
    humor: 0.5,
    verbosity: 0.5,
    creativity: 0.5,
    directness: 0.5
  }
});

const INITIAL_COMPONENTS: Component[] = [
  { id: 'base-1', type: 'base', name: 'Base Traits' }
];

const createInitialTestInfo = () => ({
  tokensUsed: null as number | null,
  warnings: [] as string[],
  message: null as string | null,
  timestamp: null as string | null
});

const PersonalityBuilder = () => {
  const [components, setComponents] = useState<Component[]>(INITIAL_COMPONENTS);
  
  const [personality, setPersonality] = useState<Personality>(createInitialPersonality());

  const [showExportModal, setShowExportModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [previewMessage, setPreviewMessage] = useState("Hi, how are you?");
  const [previewResponse, setPreviewResponse] = useState<string>('');
  const [lastTestInfo, setLastTestInfo] = useState(createInitialTestInfo());
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [showWhyPanel, setShowWhyPanel] = useState(true);
  const [exportFormat, setExportFormat] = useState('json');
  const [isDeploying, setIsDeploying] = useState(false);

  const resetBuilder = () => {
    setPersonality(createInitialPersonality());
    setComponents(INITIAL_COMPONENTS.map(c => ({ ...c })));
    setPreviewMessage("Hi, how are you?");
    setPreviewResponse('');
    setCompiledPrompt('');
    setLastTestInfo(createInitialTestInfo());
  };
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [compiledPrompt, setCompiledPrompt] = useState<string>('');
  const [isCompiling, setIsCompiling] = useState(false);
  
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef(0);

  const preserveScrollPosition = () => {
    if (mainScrollRef.current) {
      scrollPositionRef.current = mainScrollRef.current.scrollTop;
    }
  };

  const restoreScrollPosition = () => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = scrollPositionRef.current;
    }
  };

  const updatePersonalityState = (updater: (prev: Personality) => Personality) => {
    preserveScrollPosition();
    setPersonality(prev => updater(prev));
    requestAnimationFrame(() => restoreScrollPosition());
  };

  const availableComponents: AvailableComponent[] = [
    { type: 'traits', icon: Settings, label: 'Additional Traits', color: 'bg-blue-50 border-blue-200 hover:bg-blue-100' },
    { type: 'evolution', icon: TrendingUp, label: 'Personality Evolution (v1.1)', color: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100', badge: 'v1.1' },
    { type: 'memory', icon: Brain, label: 'Memory System (v1.1)', color: 'bg-green-50 border-green-200 hover:bg-green-100', badge: 'v1.1' },
    { type: 'affinity', icon: Heart, label: 'Affinity & Relationships (v1.1)', color: 'bg-pink-50 border-pink-200 hover:bg-pink-100', badge: 'v1.1' },
    { type: 'style', icon: MessageSquare, label: 'Language Style', color: 'bg-purple-50 border-purple-200 hover:bg-purple-100' },
    { type: 'safety', icon: Shield, label: 'Safety Rules', color: 'bg-red-50 border-red-200 hover:bg-red-100' }
  ];

  const archetypeOptions = ['sage', 'explorer', 'rebel', 'caregiver', 'everyman'] as const;
  const temperamentOptions = ['calm', 'energetic', 'serious', 'playful', 'thoughtful', 'spontaneous'] as const;
  const communicationStyleOptions = ['conversational', 'formal', 'casual', 'technical', 'poetic', 'direct'] as const;
  const syntaxOptions = ['simple', 'complex', 'varied', 'concise', 'elaborate'] as const;
  const punctuationOptions = ['minimal', 'moderate', 'expressive', 'academic'] as const;
  const toneOptions: ToneOption[] = ['friendly', 'professional', 'casual', 'warm', 'helpful', 'patient', 'confident', 'humorous', 'supportive', 'inspiring', 'thoughtful'];

  useEffect(() => {
    if (previewMessage.trim().length === 0) {
      setPreviewResponse('');
      setLastTestInfo({
        tokensUsed: null,
        warnings: [],
        message: null,
        timestamp: null
      });
      return;
    }

    let cancelled = false;
    setIsPreviewLoading(true);

    const generatePreview = async () => {
      try {
        const officialSchema = transformToOfficialSchema(personality);
        const testResult = await testPersonality(officialSchema, previewMessage);
        if (cancelled) return;

        if (testResult.success && testResult.validation?.valid && testResult.test_response) {
          setPreviewResponse(testResult.test_response);
          setLastTestInfo({
            tokensUsed: testResult.tokens_used ?? null,
            warnings: testResult.validation?.warnings || [],
            message: null,
            timestamp: testResult.timestamp || null
          });
        } else if (testResult.validation && !testResult.validation.valid) {
          const errors = testResult.validation.errors.join(' | ');
          setPreviewResponse(`⚠️ Validación fallida: ${errors}`);
          setLastTestInfo({
            tokensUsed: null,
            warnings: testResult.validation.warnings || [],
            message: `Validación fallida: ${errors}`,
            timestamp: testResult.timestamp || null
          });
        } else if (testResult.message) {
          setPreviewResponse(`⚠️ ${testResult.message}`);
          setLastTestInfo({
            tokensUsed: null,
            warnings: testResult.validation?.warnings || [],
            message: testResult.message,
            timestamp: testResult.timestamp || null
          });
        } else {
          const fallback = generatePreviewResponse(personality, previewMessage);
          setPreviewResponse(fallback);
          setLastTestInfo({
            tokensUsed: null,
            warnings: [],
            message: 'Vista previa local en uso. No se recibió respuesta de la API.',
            timestamp: null
          });
        }
      } catch (error: any) {
        console.error('Preview generation error:', error);
        if (cancelled) return;
        const fallback = generatePreviewResponse(personality, previewMessage);
        const validationErrors = error?.validation?.errors;
        const errorMessage = validationErrors?.length
          ? `⚠️ ${validationErrors.join(' | ')}`
          : error?.message || 'No fue posible contactar con la API.';
        setPreviewResponse(`${fallback}\n\n${errorMessage}`);
        setLastTestInfo({
          tokensUsed: null,
          warnings: [],
          message: validationErrors?.length
            ? `Validación fallida: ${validationErrors.join(' | ')}`
            : error?.message || 'No fue posible contactar con la API.',
          timestamp: null
        });
      } finally {
        if (!cancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(generatePreview, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [previewMessage, personality]);

  useEffect(() => {
    if (showExportModal && !compiledPrompt) {
      setIsCompiling(true);
      generatePersonalityPrompt(personality)
        .then(setCompiledPrompt)
        .catch((error) => {
          console.error('Failed to compile prompt:', error);
          setCompiledPrompt('');
        })
        .finally(() => setIsCompiling(false));
    }
  }, [showExportModal, personality]);

  const handleExportJSON = () => {
    const jsonContent = generatePersonalityJSON(personality);
    const filename = `${personality.name.toLowerCase().replace(/\s+/g, '_')}.json`;
    downloadFile(jsonContent, filename);
  };

  const handleExportPrompt = async () => {
    const prompt = compiledPrompt || await generatePersonalityPrompt(personality);
    const filename = `${personality.name.toLowerCase().replace(/\s+/g, '_')}_prompt.txt`;
    downloadFile(prompt, filename, 'text/plain');
  };

  const handleExportPython = () => {
    const code = generatePythonCode(personality);
    const filename = `${personality.name.toLowerCase().replace(/\s+/g, '_')}_integration.py`;
    downloadFile(code, filename, 'text/x-python');
  };

  const addComponent = (type: string) => {
    const alreadyExists = components.some(c => c.type === type);
    if (alreadyExists && type !== 'base') {
      return;
    }
    const newComponent: Component = {
      id: `${type}-${Date.now()}`,
      type,
      name: availableComponents.find(c => c.type === type)?.label || type
    };
    setComponents([...components, newComponent]);
    
    if (type === 'memory') {
      setPersonality({ 
        ...personality, 
        memory_preferences: {
          fact_categories: ["personal_info", "preferences", "goals"],
          episode_types: ["milestone", "achievement", "emotional_moment"],
          importance_threshold: 6.0
        }
      });
    }
    if (type === 'affinity' || type === 'evolution') {
      setPersonality({ 
        ...personality, 
        hierarchical_config: {
          enabled: true,
          relationship_levels: [
            { name: 'stranger', affinity_range: [0, 20], modifiers: { advanced_parameters: { formality: 0.3, humor: -0.2 } } },
            { name: 'friend', affinity_range: [41, 60], modifiers: { advanced_parameters: { formality: -0.2, humor: 0.2 } } },
            { name: 'best_friend', affinity_range: [81, 100], modifiers: { advanced_parameters: { formality: -0.4, humor: 0.4 } } }
          ]
        }
      });
    }
  };

  const removeComponent = (id: string) => {
    const component = components.find(c => c.id === id);
    setComponents(components.filter(c => c.id !== id));
    
    if (component?.type === 'memory') {
      const { memory_preferences, ...rest } = personality;
      setPersonality(rest);
    }
    if (component?.type === 'affinity' || component?.type === 'evolution') {
      const { hierarchical_config, ...rest } = personality;
      setPersonality(rest);
    }
  };

  const updatePersonality = (key: keyof Personality | string, value: any) => {
    updatePersonalityState(prev => ({ ...prev, [key]: value } as Personality));
  };

  const updateAdvancedParameters = (key: keyof Personality['advanced_parameters'] | string, value: number) => {
    updatePersonalityState(prev => ({
      ...prev,
      advanced_parameters: {
        ...prev.advanced_parameters,
        [key]: value
      }
    }));
  };

  const updateCoreTrait = (field: 'archetype' | 'temperament' | 'communication_style', value: string) => {
    updatePersonalityState(prev => ({
      ...prev,
      core_traits: {
        ...(prev.core_traits || {}),
        [field]: value as any
      }
    }));
  };

  const updateLinguisticProfile = (field: 'tone' | 'syntax' | 'vocabulary' | 'fillers' | 'punctuation_style', value: any) => {
    updatePersonalityState(prev => ({
      ...prev,
      linguistic_profile: {
        ...(prev.linguistic_profile || {}),
        [field]: value
      }
    }));
  };

  const toggleTone = (tone: ToneOption) => {
    updatePersonalityState(prev => {
      const currentTones = prev.linguistic_profile?.tone || [];
      const exists = currentTones.includes(tone);
      const nextTones = exists ? currentTones.filter(t => t !== tone) : [...currentTones, tone];

      return {
        ...prev,
        linguistic_profile: {
          ...(prev.linguistic_profile || {}),
          tone: nextTones
        }
      };
    });
  };

  const updateBehavioralRule = (index: number, value: string) => {
    updatePersonalityState(prev => {
      const rules = [...(prev.behavioral_rules || [])];
      rules[index] = value;
      return {
        ...prev,
        behavioral_rules: rules
      };
    });
  };

  const addBehavioralRule = () => {
    updatePersonalityState(prev => ({
      ...prev,
      behavioral_rules: [...(prev.behavioral_rules || []), '']
    }));
  };

  const removeBehavioralRule = (index: number) => {
    updatePersonalityState(prev => ({
      ...prev,
      behavioral_rules: (prev.behavioral_rules || []).filter((_, i) => i !== index)
    }));
  };

  const updateForbiddenTopics = (topicsText: string) => {
    const topics = topicsText
      .split(',')
      .map(topic => topic.trim())
      .filter(topic => topic.length > 0);

    updatePersonalityState(prev => ({
      ...prev,
      safety_guards: {
        ...(prev.safety_guards || {}),
        forbidden_topics: topics
      }
    }));
  };

  const TraitSlider = ({ 
    label, 
    value, 
    onChange, 
    leftLabel, 
    rightLabel, 
    leftEmoji, 
    rightEmoji 
  }: {
    label: string;
    value: number;
    onChange: (value: number) => void;
    leftLabel: string;
    rightLabel: string;
    leftEmoji: string;
    rightEmoji: string;
  }) => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className="text-sm text-gray-500">{value.toFixed(1)}</span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.1"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
      />
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{leftEmoji} {leftLabel}</span>
        <span>{rightLabel} {rightEmoji}</span>
      </div>
    </div>
  );

  const ComponentCard = ({ component }: { component: Component }) => {
    if (component.type === 'base') {
      return (
        <div className="bg-white rounded-lg border-2 border-blue-200 p-4 mb-3 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">{component.name}</h3>
            </div>
          </div>
          
          <TraitSlider
            label="Empathy"
            value={personality.advanced_parameters.empathy}
            onChange={(v) => updateAdvancedParameters('empathy', v)}
            leftLabel="Logical"
            rightLabel="Emotional"
            leftEmoji="🤖"
            rightEmoji="💕"
          />
          
          <TraitSlider
            label="Formality"
            value={personality.advanced_parameters.formality}
            onChange={(v) => updateAdvancedParameters('formality', v)}
            leftLabel="Casual"
            rightLabel="Professional"
            leftEmoji="👕"
            rightEmoji="👔"
          />
          
          <TraitSlider
            label="Humor"
            value={personality.advanced_parameters.humor}
            onChange={(v) => updateAdvancedParameters('humor', v)}
            leftLabel="Serious"
            rightLabel="Funny"
            leftEmoji="😐"
            rightEmoji="😄"
          />
          
          <TraitSlider
            label="Verbosity"
            value={personality.advanced_parameters.verbosity}
            onChange={(v) => updateAdvancedParameters('verbosity', v)}
            leftLabel="Concise"
            rightLabel="Detailed"
            leftEmoji="📝"
            rightEmoji="📚"
          />

          <TraitSlider
            label="Directness"
            value={personality.advanced_parameters.directness}
            onChange={(v) => updateAdvancedParameters('directness', v)}
            leftLabel="Indirect"
            rightLabel="Direct"
            leftEmoji="🌫️"
            rightEmoji="📍"
          />

          <TraitSlider
            label="Creatividad"
            value={personality.advanced_parameters.creativity}
            onChange={(v) => updateAdvancedParameters('creativity', v)}
            leftLabel="Literal"
            rightLabel="Creativo"
            leftEmoji="📐"
            rightEmoji="🎨"
          />
        </div>
      );
    }

    if (component.type === 'traits') {
      return (
        <div className="bg-white rounded-lg border-2 border-blue-200 p-4 mb-3 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">{component.name}</h3>
            </div>
            <button 
              type="button"
              onClick={() => removeComponent(component.id)} 
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Archetype</label>
              <select
                value={personality.core_traits?.archetype || ''}
                onChange={(e) => updateCoreTrait('archetype', e.target.value)}
                className="w-full text-sm border border-blue-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {archetypeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Temperament</label>
              <select
                value={personality.core_traits?.temperament || ''}
                onChange={(e) => updateCoreTrait('temperament', e.target.value)}
                className="w-full text-sm border border-blue-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {temperamentOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Communication Style</label>
              <select
                value={personality.core_traits?.communication_style || ''}
                onChange={(e) => updateCoreTrait('communication_style', e.target.value)}
                className="w-full text-sm border border-blue-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {communicationStyleOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Reglas de comportamiento</label>
              <button
                type="button"
                onClick={addBehavioralRule}
                className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                + Add rule
              </button>
            </div>
            <div className="space-y-2">
              {(personality.behavioral_rules || []).length === 0 && (
                <p className="text-xs text-gray-400 italic">Aún no hay reglas. Añade al menos una para guiar el comportamiento.</p>
              )}
              {(personality.behavioral_rules || []).map((rule, index) => (
                <div key={index} className="flex items-start gap-2">
                  <textarea
                    value={rule}
                    onChange={(e) => updateBehavioralRule(index, e.target.value)}
                    className="flex-1 text-sm border border-blue-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                    placeholder="Describe the rule..."
                  />
                  <button
                    type="button"
                    onClick={() => removeBehavioralRule(index)}
                    className="text-red-400 hover:text-red-600 transition-colors mt-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-800">
              <strong>Consejo:</strong> Estas configuraciones se incluyen directamente en el JSON oficial para que el LLM comprenda la personalidad.
            </p>
          </div>
        </div>
      );
    }

    if (component.type === 'memory') {
      return (
        <div className="bg-white rounded-lg border-2 border-green-200 p-4 mb-3 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-gray-800">Memory System</h3>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">v1.1</span>
            </div>
            <button 
              type="button"
              onClick={() => removeComponent(component.id)} 
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-2">What it remembers:</p>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• Personal information (name, age, location)</div>
                <div>• User preferences and interests</div>
                <div>• Past conversations and topics</div>
                <div>• Emotional moments and achievements</div>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Storage configuration (SQLite, PostgreSQL, etc.) is a backend setting, not part of the personality JSON. Configure it when setting up your backend.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (component.type === 'affinity' || component.type === 'evolution') {
      const levels = personality.hierarchical_config?.relationship_levels || [];
      
      const updateRelationshipLevel = (index: number, field: string, value: any, event?: React.SyntheticEvent) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        
        const newLevels = [...levels];
        if (field === 'name') {
          newLevels[index] = { ...newLevels[index], name: value };
        } else if (field === 'min') {
          const minValue = parseInt(value, 10);
          newLevels[index] = { 
            ...newLevels[index], 
            affinity_range: [isNaN(minValue) ? 0 : minValue, newLevels[index].affinity_range[1]]
          };
        } else if (field === 'max') {
          const maxValue = parseInt(value, 10);
          newLevels[index] = { 
            ...newLevels[index], 
            affinity_range: [newLevels[index].affinity_range[0], isNaN(maxValue) ? 0 : maxValue]
          };
        } else if (field.startsWith('modifier_')) {
          const paramName = field.replace('modifier_', '');
          newLevels[index] = {
            ...newLevels[index],
            modifiers: {
              ...newLevels[index].modifiers,
              advanced_parameters: {
                ...newLevels[index].modifiers.advanced_parameters,
                [paramName]: parseFloat(value)
              }
            }
          };
        }
        
        setPersonality({
          ...personality,
          hierarchical_config: {
            ...personality.hierarchical_config!,
            relationship_levels: newLevels
          }
        });
      };

      const palette = component.type === 'affinity'
        ? {
            border: 'border-pink-200',
            pill: 'bg-pink-50',
            buttonBg: 'bg-pink-100',
            buttonText: 'text-pink-700',
            infoBg: 'bg-blue-50',
            infoBorder: 'border-blue-200'
          }
        : {
            border: 'border-indigo-200',
            pill: 'bg-indigo-50',
            buttonBg: 'bg-indigo-100',
            buttonText: 'text-indigo-700',
            infoBg: 'bg-indigo-50',
            infoBorder: 'border-indigo-200'
          };

      const addRelationshipLevel = () => {
        const newLevel = {
          name: 'new_level',
          affinity_range: [0, 10] as [number, number],
          modifiers: {
            advanced_parameters: {
              formality: 0,
              humor: 0,
              directness: 0,
              creativity: 0
            }
          }
        };
        
        setPersonality({
          ...personality,
          hierarchical_config: {
            ...personality.hierarchical_config!,
            relationship_levels: [...levels, newLevel]
          }
        });
      };

      const removeRelationshipLevel = (index: number) => {
        const newLevels = levels.filter((_, i) => i !== index);
        setPersonality({
          ...personality,
          hierarchical_config: {
            ...personality.hierarchical_config!,
            relationship_levels: newLevels
          }
        });
      };

      return (
        <div className={`bg-white rounded-lg border-2 ${palette.border} p-4 mb-3 shadow-sm`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {component.type === 'affinity' ? (
                <Heart className="w-5 h-5 text-pink-600" />
              ) : (
                <TrendingUp className="w-5 h-5 text-indigo-600" />
              )}
              <h3 className="font-semibold text-gray-800">
                {component.type === 'affinity' ? 'Affinity System' : 'Personality Evolution'}
              </h3>
              <span className={`text-xs ${component.type === 'affinity' ? 'bg-pink-100 text-pink-700' : 'bg-indigo-100 text-indigo-700'} px-2 py-1 rounded font-medium`}>v1.1</span>
            </div>
            <button 
              type="button"
              onClick={() => removeComponent(component.id)} 
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Relationship Levels</label>
                <button
                  type="button"
                  onClick={addRelationshipLevel}
                  className={`text-xs px-2 py-1 ${palette.buttonBg} ${palette.buttonText} rounded hover:opacity-90 transition-colors`}
                >
                  + Add Level
                </button>
              </div>
              
              {levels.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-sm">
                  No {component.type === 'affinity' ? 'relationship' : 'evolution'} levels configured. Click "Add Level" to create one.
                </div>
              ) : (
                <div className="space-y-3">
                  {levels.map((level, index) => (
                    <div key={index} className={`border ${palette.border} ${palette.pill} rounded-lg p-3`}>
                      <div className="flex items-center justify-between mb-2">
                        <input
                          type="text"
                          value={level.name}
                          onChange={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            updateRelationshipLevel(index, 'name', e.target.value);
                          }}
                          placeholder="Level name (e.g., stranger)"
                          className="text-sm font-medium text-gray-800 bg-transparent border-b border-pink-300 focus:border-pink-500 outline-none px-1"
                        />
                        {levels.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRelationshipLevel(index)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Min Points</label>
                          <input
                            type="number"
                            value={level.affinity_range[0]}
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateRelationshipLevel(index, 'min', e.target.value);
                            }}
                            min="0"
                            max="100"
                            className="w-full px-2 py-1 text-xs border border-pink-300 rounded focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Max Points</label>
                          <input
                            type="number"
                            value={level.affinity_range[1]}
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateRelationshipLevel(index, 'max', e.target.value);
                            }}
                            min="0"
                            max="100"
                            className="w-full px-2 py-1 text-xs border border-pink-300 rounded focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-pink-200">
                        <p className="text-xs text-gray-600 mb-2">Parameter Modifiers:</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Formality</label>
                            <input
                              type="number"
                              step="0.1"
                              value={level.modifiers.advanced_parameters.formality}
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                updateRelationshipLevel(index, 'modifier_formality', e.target.value);
                              }}
                              min="-1"
                              max="1"
                              className="w-full px-2 py-1 text-xs border border-pink-300 rounded focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Humor</label>
                            <input
                              type="number"
                              step="0.1"
                              value={level.modifiers.advanced_parameters.humor}
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                updateRelationshipLevel(index, 'modifier_humor', e.target.value);
                              }}
                              min="-1"
                              max="1"
                              className="w-full px-2 py-1 text-xs border border-pink-300 rounded focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className={`p-3 ${palette.infoBg} rounded-lg border ${palette.infoBorder} rounded-lg`}>
              <p className="text-xs text-gray-700">
                <strong>💡 Info:</strong> {component.type === 'affinity'
                  ? 'Relationship levels adjust advanced parameters based on accumulated affinity (range 0-100). Use modifiers between -1.0 and 1.0.'
                  : 'Personality evolution lets you define phases with progressive modifiers. Adjust affinity ranges to control transitions.'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (component.type === 'style') {
      return (
        <div className="bg-white rounded-lg border-2 border-purple-200 p-4 mb-3 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-800">{component.name}</h3>
            </div>
            <button 
              type="button"
              onClick={() => removeComponent(component.id)} 
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2 block">Tone</label>
              <div className="flex flex-wrap gap-2">
                {toneOptions.map((tone) => {
                  const isActive = personality.linguistic_profile?.tone?.includes(tone);
                  return (
                    <button
                      key={tone}
                      type="button"
                      onClick={() => toggleTone(tone)}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${isActive ? 'bg-purple-600 text-white border-purple-600' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'}`}
                    >
                      {tone}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Syntax</label>
                <select
                  value={personality.linguistic_profile?.syntax || ''}
                  onChange={(e) => updateLinguisticProfile('syntax', e.target.value)}
                  className="w-full text-sm border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {syntaxOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Punctuation</label>
                <select
                  value={personality.linguistic_profile?.punctuation_style || ''}
                  onChange={(e) => updateLinguisticProfile('punctuation_style', e.target.value)}
                  className="w-full text-sm border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {punctuationOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Preferred Vocabulary</label>
              <textarea
                value={(personality.linguistic_profile?.vocabulary || []).join(', ')}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateLinguisticProfile('vocabulary', e.target.value.split(',').map(word => word.trim()).filter(Boolean));
                }}
                className="w-full text-sm border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={2}
                placeholder="Separar por comas (ej: excelente, analicemos, perspectiva)"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Optional Fillers</label>
              <textarea
                value={(personality.linguistic_profile?.fillers || []).join(', ')}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateLinguisticProfile('fillers', e.target.value.split(',').map(word => word.trim()).filter(Boolean));
                }}
                className="w-full text-sm border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={2}
                placeholder="Optional. Separate with commas."
              />
            </div>
          </div>
        </div>
      );
    }

    if (component.type === 'safety') {
      return (
        <div className="bg-white rounded-lg border-2 border-red-200 p-4 mb-3 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-gray-800">{component.name}</h3>
            </div>
            <button 
              type="button"
              onClick={() => removeComponent(component.id)} 
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">Forbidden Topics</label>
              <textarea
                value={(personality.safety_guards?.forbidden_topics || []).join(', ')}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateForbiddenTopics(e.target.value);
                }}
                className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={2}
                placeholder="Separate by commas (e.g., politics, religion, personal finance)"
              />
            </div>
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              Estas reglas se exportan dentro de `safety_guards.forbidden_topics` para reforzar la moderación.
            </p>
          </div>
        </div>
      );
    }

    return null;
  };


  const renderComponentsPanel = ({ variant = 'desktop' }: { variant?: 'desktop' | 'mobile' } = {}) => (
    <div className={`flex flex-col ${variant === 'desktop' ? 'h-full' : 'gap-4'}`}>
      <div className={`${variant === 'desktop' ? 'mb-4' : 'mb-3'}`}>
        <h2 className="text-lg font-bold text-gray-800 mb-1">Components</h2>
        <p className="text-xs text-gray-600">Click to add</p>
      </div>

      <div className={variant === 'mobile' ? 'grid gap-3 sm:grid-cols-2' : 'space-y-2'}>
        {availableComponents.map(({ type, icon: Icon, label, color, badge }) => {
          const isAdded = components.some(c => c.type === type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => addComponent(type)}
              disabled={isAdded}
              className={`w-full flex items-center gap-3 p-3 border rounded-lg transition-all ${color} ${isAdded ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Icon className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700 flex-1 text-left">{label}</span>
              {badge && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium">{badge}</span>}
            </button>
          );
        })}
      </div>

      <div className={`${variant === 'desktop' ? 'mt-6 pt-6' : 'pt-4'} border-t border-gray-200`}>
        <button 
          type="button"
          onClick={() => setShowTemplateModal(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors shadow-sm"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-medium">Use Template</span>
        </button>
      </div>
    </div>
  );

  const renderPreviewPanel = ({ variant = 'desktop' }: { variant?: 'desktop' | 'mobile' } = {}) => (
    <div>
      <h2 className={`text-lg font-bold text-gray-800 mb-4 ${variant === 'mobile' ? 'text-center sm:text-left' : ''}`}>Live Preview</h2>
      
      <div className="mb-4">
        <label className={`text-sm font-medium text-gray-700 mb-2 block ${variant === 'mobile' ? 'text-center sm:text-left' : ''}`}>Test Message</label>
        <input
          type="text"
          value={previewMessage}
          onChange={(e) => setPreviewMessage(e.target.value)}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${variant === 'mobile' ? 'text-center sm:text-left' : ''}`}
          placeholder="Type a message..."
        />
      </div>

      <div className="space-y-3 mb-6">
        <div className="bg-gray-100 rounded-lg p-3">
          <p className="text-xs text-gray-500 mb-1">User</p>
          <p className="text-sm text-gray-800">{previewMessage}</p>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-600 mb-1 font-medium">AI ({personality.name})</p>
          {isPreviewLoading ? (
            <p className="text-sm text-gray-500 italic">Generating response via API...</p>
          ) : previewResponse ? (
            <p className="text-sm text-gray-800 whitespace-pre-line">{previewResponse}</p>
          ) : (
            <p className="text-sm text-gray-400 italic">Type a message to see the AI's response</p>
          )}
        </div>
      </div>

      <div className="space-y-2 mb-6 p-3 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700">Statistics</h3>
        <div className="text-xs space-y-1 text-gray-600">
          <div className="flex justify-between">
            <span>Completeness:</span>
            <span className="font-medium">{getPersonalityCompleteness(personality)}%</span>
          </div>
          <div className="flex justify-between">
            <span>Components:</span>
            <span className="font-medium">{components.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Version:</span>
            <span className="font-medium">{personality.hierarchical_config?.enabled || personality.memory_preferences ? 'v1.1' : 'v1.0'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <button 
          type="button"
          onClick={() => setShowExportModal(true)}
          className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 text-sm font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export Options
        </button>
        <button
          type="button"
          onClick={resetBuilder}
          className="w-full px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium transition-colors"
        >
          🔄 Reset Builder
        </button>
      </div>
    </div>
  );


  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* HEADER */}
      <header className="bg-white border-b border-gray-200 px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">LuminoraCore</h1>
              <p className="text-sm text-gray-500">Deploy Minds, Not Prompts</p>
            </div>
          </div>
          
          <a 
            href="https://github.com/luminoracore" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors self-start sm:self-auto"
          >
            <Github className="w-5 h-5" />
            <span className="text-sm font-medium">GitHub</span>
          </a>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* Left Panel */}
        <aside className="hidden lg:flex lg:w-72 xl:w-80 bg-white border-b lg:border-b-0 lg:border-r border-gray-200">
          <div className="w-full h-full overflow-y-auto p-4">
            {renderComponentsPanel({ variant: 'desktop' })}
          </div>
        </aside>

        {/* Center Panel */}
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
          <div className="lg:hidden mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              {renderComponentsPanel({ variant: 'mobile' })}
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center sm:text-left">Build Your AI Personality</h1>
              <input
                type="text"
                value={personality.name}
                onChange={(e) => updatePersonality('name', e.target.value)}
                className="w-full text-lg font-medium text-gray-600 border-b-2 border-transparent hover:border-blue-300 focus:border-blue-500 outline-none px-2 py-1 transition-colors text-center sm:text-left"
                placeholder="Name your personality..."
              />
            </div>

            {components.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Plus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Click on a component from the left to get started</p>
              </div>
            ) : (
              components.map(component => (
                <ComponentCard key={component.id} component={component} />
              ))
            )}
            
            <button
              type="button"
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
              onClick={() => {}}
            >
              <Plus className="w-5 h-5 mx-auto mb-1" />
              <span className="text-sm">Add Component</span>
            </button>
          </div>

          <div className="lg:hidden mt-8">
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              {renderPreviewPanel({ variant: 'mobile' })}
            </div>
          </div>
        </main>

        {/* Right Panel */}
        <aside className="hidden lg:block lg:w-80 bg-white border-l border-gray-200 p-4 overflow-y-auto">
          {renderPreviewPanel({ variant: 'desktop' })}
        </aside>
      </div>

      {/* FOOTER */}
      <footer className="bg-white border-t border-gray-200 px-4 py-4 sm:px-6">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <p className="text-sm text-gray-600">
            Powered by <span className="font-semibold text-gray-900">LuminoraCore SDK</span>
          </p>
        </div>
        
        <p className="text-xs text-gray-500 text-center mb-3">
          Modular, persistent, production-grade personality engine
        </p>
        
        <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
          <a href="https://github.com/luminoracore" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 transition-colors flex items-center gap-1">
            <Github className="w-4 h-4" />
            <span>GitHub</span>
          </a>
          <a href="https://luminoracore.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 transition-colors flex items-center gap-1">
            <Shield className="w-4 h-4" />
            <span>Privacy Policy</span>
          </a>
        </div>
        
        <p className="text-xs text-gray-400 text-center mt-3">
          © 2025 LuminoraCore by Ereace. All rights reserved.
        </p>
      </footer>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Choose a Template</h2>
                <button 
                  type="button"
                  onClick={() => setShowTemplateModal(false)} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-6">
                Select a pre-configured personality template to get started quickly. You can customize it afterwards.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getTemplates().map((template) => (
                    <button
                      type="button"
                    key={template.id}
                    onClick={() => {
                      const templateConfig = applyTemplate(template);
                      setPersonality({
                        ...personality,
                        ...templateConfig
                      });
                      
                      const templateComponents = template.components.map((compType, idx) => ({
                        id: `${compType}-${Date.now()}-${idx}`,
                        type: compType,
                        name: availableComponents.find(c => c.type === compType)?.label || compType
                      }));
                      
                      const baseComponent = components.find(c => c.type === 'base');
                      setComponents([
                        ...(baseComponent ? [baseComponent] : []),
                        ...templateComponents.filter(c => c.type !== 'base')
                      ]);
                      
                      setShowTemplateModal(false);
                    }}
                    className="text-left border-2 border-gray-200 rounded-lg p-4 hover:border-purple-400 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">{template.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-800">{template.name}</h3>
                        <p className="text-xs text-gray-500">{template.components.length} components</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Export Options</h2>
                <button 
                  type="button"
                  onClick={() => {
                    setShowExportModal(false);
                    setCompiledPrompt('');
                  }} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">Personality JSON</h3>
                      <p className="text-xs text-gray-600">Complete personality configuration</p>
                    </div>
                    <button
                      onClick={handleExportJSON}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 text-sm font-medium transition-colors"
                    >
                      Download JSON
                    </button>
                  </div>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto max-h-32">
                    {generatePersonalityJSON(personality)}
                  </pre>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">Compiled Prompt</h3>
                      <p className="text-xs text-gray-600">Ready to use with any LLM</p>
                    </div>
                    <button
                      onClick={handleExportPrompt}
                      disabled={isCompiling}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      Download Prompt
                    </button>
                  </div>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto max-h-32 whitespace-pre-wrap">
                    {isCompiling ? 'Compiling...' : (compiledPrompt || 'Click download')}
                  </pre>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">Python Integration</h3>
                      <p className="text-xs text-gray-600">Using LuminoraCore SDK</p>
                    </div>
                    <button
                      onClick={handleExportPython}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 text-sm font-medium transition-colors"
                    >
                      Download Python
                    </button>
                  </div>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto max-h-32">
                    {generatePythonCode(personality)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalityBuilder;